import React from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';

import styles from './styles.module.scss';

import { Rating } from '@components/_shared/rating';
import { TextButton } from '@components/_shared/textButton';
import { getListingTitle } from '@utils/helpers';
import { colors } from '@utils/constants/colors';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { ListingType } from '@utils/types/listing';
import { DatesRange } from '@utils/types/calendar';
import { ListingBaths, ListingGuests, ListingBeds } from '@assets/icons';

const Logo = () => (
  <svg
    width="47"
    height="12"
    viewBox="0 0 47 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.55623 11.1494L2.97455 6.85328H1.04949V11.1494H0.00488281V0.865259H3.13871C4.3027 0.865259 5.18315 1.13989 5.78007 1.68916C6.38694 2.23843 6.69037 2.95935 6.69037 3.85192C6.69037 4.59735 6.4715 5.2349 6.03376 5.76455C5.60597 6.2844 4.96926 6.62279 4.12362 6.77972L6.80976 11.1494H5.55623ZM1.04949 6.01467H3.15363C3.96942 6.01467 4.58126 5.8185 4.98916 5.42616C5.407 5.03383 5.61592 4.50908 5.61592 3.85192C5.61592 3.17514 5.41695 2.65529 5.019 2.29238C4.62106 1.91966 3.99429 1.7333 3.13871 1.7333H1.04949V6.01467Z"
      fill="#1D1919"
    />
    <path
      d="M17.1799 11.2523C16.2149 11.2523 15.3394 11.0317 14.5535 10.5903C13.7676 10.1391 13.1458 9.51626 12.6881 8.72178C12.2404 7.9175 12.0166 7.01022 12.0166 5.99995C12.0166 4.98969 12.2404 4.08732 12.6881 3.29284C13.1458 2.48855 13.7676 1.86572 14.5535 1.42434C15.3394 0.973151 16.2149 0.747559 17.1799 0.747559C18.1549 0.747559 19.0354 0.973151 19.8213 1.42434C20.6072 1.86572 21.2241 2.48855 21.6718 3.29284C22.1194 4.08732 22.3433 4.98969 22.3433 5.99995C22.3433 7.01022 22.1194 7.9175 21.6718 8.72178C21.2241 9.51626 20.6072 10.1391 19.8213 10.5903C19.0354 11.0317 18.1549 11.2523 17.1799 11.2523ZM17.1799 10.3549C17.9559 10.3549 18.6523 10.1783 19.2692 9.82523C19.886 9.47213 20.3735 8.96699 20.7316 8.30983C21.0898 7.64286 21.2688 6.8729 21.2688 5.99995C21.2688 5.12701 21.0898 4.36195 20.7316 3.70479C20.3735 3.04763 19.886 2.54249 19.2692 2.18939C18.6523 1.83629 17.9559 1.65974 17.1799 1.65974C16.4039 1.65974 15.7075 1.83629 15.0907 2.18939C14.4739 2.54249 13.9864 3.04763 13.6283 3.70479C13.2701 4.36195 13.091 5.12701 13.091 5.99995C13.091 6.8729 13.2701 7.64286 13.6283 8.30983C13.9864 8.96699 14.4739 9.47213 15.0907 9.82523C15.7075 10.1783 16.4039 10.3549 17.1799 10.3549Z"
      fill="#1D1919"
    />
    <path
      d="M36.2528 0.865259L32.2982 11.1494H31.1044L27.1498 0.865259H28.2839L31.7013 10.0312L35.1485 0.865259H36.2528Z"
      fill="#1D1919"
    />
    <path
      d="M42.5977 1.70388V5.54386H46.5523V6.39719H42.5977V10.296H47V11.1494H41.5531V0.850546H47V1.70388H42.5977Z"
      fill="#1D1919"
    />
  </svg>
);

type Props = {
  listing: ListingType;
  onViewOnMapClick?: () => void;
  scrollToReviews?: () => void;
  /**
   * If passed, we should show a reserved dates alongside the listing title
   */
  reservationDates?: DatesRange;
} & BaseComponentProps;

const ListingTitleAndFeatures = (props: Props) => {
  const {
    listing,
    onViewOnMapClick,
    scrollToReviews,
    reservationDates,
    containerStyle,
  } = props;

  return (
    <div className={clsx([containerStyle])}>
      {reservationDates && (
        <div className={styles.subtitle}>
          <strong>Booking</strong> <span>•</span>
          <span>
            {`${format(new Date(reservationDates.from!), 'MMM d')} - ${format(
              new Date(reservationDates.to!),
              'MMM d'
            )}`}
          </span>
        </div>
      )}

      <h1 className={styles.title}>{getListingTitle(listing)}</h1>

      <div className={styles.features}>
        <div className={styles.featuresContainer}>
          <div
            className={clsx([
              styles.reviews,
              !!scrollToReviews &&
                !!listing.listing_reviews.length &&
                styles.clickable,
            ])}
            role="button"
            onClick={scrollToReviews}
          >
            <Rating
              showAllStars
              showDescription={false}
              rate={Number(listing.rating)}
              color={colors.roveLightBlue}
              containerStyle={styles.ratingContainer}
            />
            <span>{listing.listing_reviews.length}&nbsp;reviews</span>
          </div>

          <div className={styles.feature}>
            <ListingGuests className={styles.mobileIcon} />
            <span>{listing.accommodates}</span>
            <span className={styles.desktopOnly}>&nbsp;guests</span>
          </div>

          <div className={styles.feature}>
            <ListingBeds className={styles.mobileIcon} />
            <span>{listing.bedrooms_total}</span>
            <span className={styles.desktopOnly}>&nbsp;bedrooms</span>
          </div>

          <div className={styles.feature}>
            <ListingBaths className={styles.mobileIcon} />
            <span>{listing.bathrooms}</span>
            <span className={styles.desktopOnly}>&nbsp;bathrooms</span>
          </div>

          {!!listing.offices_total && (
            <div className={clsx([styles.feature, styles.desktopOnly])}>
              <span>{listing.offices_total}</span>
              <span>
                &nbsp; workstation{Number(listing.offices_total) > 1 ? 's' : ''}
              </span>
            </div>
          )}

          <div className={styles.feature}>
            <span>{listing.min_stay}</span>
            <span>&nbsp;day min. stay</span>
          </div>

          <div className={clsx([styles.feature, styles.desktopOnly])}>
            <div className={styles.verifiedBy}>
              <span>Verified by</span>
              <Logo />
            </div>
          </div>

          {onViewOnMapClick && listing.address && (
            <TextButton
              label="View on map"
              onClick={onViewOnMapClick}
              containerStyle={styles.viewOnMap}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingTitleAndFeatures;
